import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { X, Receipt } from "lucide-react";

import { theme } from "../theme";
import { useT } from "../i18n";
import { apiFetch } from "../api";
import FolioEditor from "./FolioEditor";
import PaymentDetail from "./PaymentDetail";
import QuoteFooter from "./QuoteFooter";

// THE place money is touched.
//
// A payment is a first-class object; this dialog is its one screen. Before it exists the dialog is
// the composer — pick items, set quantities, apply a voucher, choose a method, create. Everything
// that used to be spread across a room card, a payment card, a deposit card and an add-on card
// happens here, which is the point: the front desk had four surfaces that could take money and no
// way to tell which one applied to the booking in front of them.
//
// The three server carriers (room-rate / deposit-collection / add-on-only) stay invisible. The FO
// ticks rows; the server picks the carrier that can hold that combination, and prices it.
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  /* Above the drawer panel (1400). */
  z-index: 1500;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  max-width: 580px;
  width: 100%;
`;

const Head = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem;
`;

const HeadMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 1.02rem;
  font-weight: 800;
  gap: 0.35rem;
  margin: 0;
`;

// The header names the GUEST and the booking, never a room — the dialog charges the whole booking,
// and a room name here was the lie that made "which room does this attach to" ambiguous.
const Sub = styled.div`
  color: #4a4f63;
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 0.1rem;
`;

const State = styled.div`
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 800;
  margin: 0.7rem 1.1rem 0;
  padding: 0.45rem 0.6rem;
  ${(props) => {
    if (props.$kind === "booking") return "background:#fdf3e3;color:#8a5a00;";
    if (props.$kind === "deposit") return "background:#eef3fb;color:#2f4a70;";
    if (props.$kind === "add_on") return "background:#e6f6ee;color:#1b6e56;";
    return "background:#f4f2ee;color:#5e6278;";
  }}
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.8rem 1.1rem;
`;

const Msg = styled.div`
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
  padding: 0.45rem 0.6rem;
  ${(props) =>
    props.$ok
      ? "background:#e6f6ee;color:#1b6e56;"
      : "background:#fdecea;color:#b42318;"}
`;

// Voucher and review discount live INSIDE the dialog, next to the total they change. They used to be
// duplicated into all three payment cards, each with its own copy of the state.
const Discounts = styled.div`
  border-top: 1px solid ${theme.border};
  margin-top: 0.5rem;
  padding-top: 0.6rem;
`;

const VoucherRow = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const VoucherInput = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  flex: 1 1 auto;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  min-width: 0;
  padding: 0.4rem 0.55rem;
  text-transform: uppercase;
`;

const SmallBtn = styled.button`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.ink700};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 800;
  padding: 0.4rem 0.7rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Check = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${theme.ink};
  cursor: pointer;
  display: flex;
  font-size: 0.8rem;
  font-weight: 700;
  gap: 0.35rem;
  margin-top: 0.5rem;
  padding: 0;
  text-align: left;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Box = styled.span`
  align-items: center;
  background: ${(props) => (props.$on ? theme.gold : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? theme.gold : theme.border)};
  border-radius: 5px;
  color: #ffffff;
  display: inline-flex;
  flex: 0 0 auto;
  height: 18px;
  justify-content: center;
  width: 18px;
`;

const Note = styled.div`
  color: #4a4f63;
  font-size: 0.76rem;
  font-weight: 600;
  margin-top: 0.3rem;
`;

const BackRow = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  justify-content: flex-end;
  padding: 0.7rem 1.1rem;
`;

const Back = styled.button`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.ink700};
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: 800;
  padding: 0.45rem 0.8rem;
`;

const PaymentDialog = (props) => {
  const { t } = useT();
  const data = props.data || {};
  // Two acts, one screen. With a payment selected the dialog shows THAT payment's lifecycle; without
  // one it composes the next payment. Reopening a payment is the same gesture as creating one, which
  // is the point — the front desk should not have to know which surface owns which stage.
  const opened = (data.payment?.payments || []).find(
    (row) => row.id === props.paymentId,
  );
  const folio = data.payment?.folio;
  const intent = data.payment?.charge_intent || {};
  const discountConfig = data.payment?.sellable_discount;

  // `picked` holds only the rows the FO has EXPLICITLY changed. Everything else falls back to the
  // server's default (selectable rows start ticked), so a refreshed folio never has to be synced into
  // client state — the row that appears after adding an extra bed is already correct.
  const [picked, setPicked] = useState({});
  const [drafts, setDrafts] = useState({});
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState("");
  const [review, setReview] = useState(false);
  // The quote is stored WITH the selection it priced. "Loading" is then a comparison, not a second
  // piece of state set from inside an effect — and the footer can never show a total that belongs to
  // a selection the FO has already changed.
  const [priced, setPriced] = useState({ key: null, quote: {} });
  const [msg, setMsg] = useState(null);

  const groups = useMemo(() => folio?.groups || [], [folio]);

  const pendingItems = useMemo(
    () =>
      groups
        .flatMap((group) => group.items)
        .filter((item) => item.state === "pending"),
    [groups],
  );

  const isPicked = (item) =>
    picked[item.key] ?? (item.selectable !== false && item.state === "pending");

  const togglePick = (item) => {
    if (item.selectable === false || item.required) return;
    setPicked((prev) => ({ ...prev, [item.key]: !isPicked(item) }));
  };

  const chosen = pendingItems.filter((item) => isPicked(item));
  // Money held in trust gets its own subtotal on every screen that shows it, and never merges into a
  // charge total.
  const heldTotal = chosen
    .filter((item) => item.kind === "deposit")
    .reduce((sum, item) => sum + Number(item.amount_rupiah || 0), 0);

  const selection = useMemo(() => {
    const of = (kind) => chosen.filter((item) => item.kind === kind);
    return {
      include_room_rate: chosen.some((item) => item.kind === "room_rate"),
      include_deposit: of("deposit").length > 0,
      bundled_rental_ids: of("rental").map((item) => item.id),
      bundled_early_checkin_ids: of("early_checkin").map((item) => item.id),
      bundled_late_checkout_ids: of("late_checkout").map((item) => item.id),
      amounts: chosen.map((item) => item.amount_rupiah),
      voucher_code: voucher || undefined,
      apply_sellable_discount: review ? "1" : "0",
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [pendingItems, picked, voucher, review]);

  const bookingId = data.booking?.id;
  const selectionKey = JSON.stringify(selection);
  const quote = priced.key === selectionKey ? priced.quote : {};
  const loading = priced.key !== selectionKey;

  // Priced on the server on every change: the online fee, cash permission and voucher eligibility all
  // depend on which carrier the selection lands on, and that changes as rows are ticked.
  useEffect(() => {
    let cancelled = false;
    apiFetch(`/frontdesk_v2/bookings/${bookingId}/folio_quote`, {
      method: "POST",
      body: JSON.stringify({ ...selection, mode: "link" }),
    })
      .then((res) => {
        if (!cancelled)
          setPriced({ key: selectionKey, quote: res?.data?.quote || {} });
      })
      .catch(() => {
        if (!cancelled) setPriced({ key: selectionKey, quote: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [selection, selectionKey, bookingId]);

  const draftQty = (item) => drafts[item.key] ?? 0;
  const setDraftQty = (item, value) =>
    setDrafts((prev) => ({ ...prev, [item.key]: Math.max(0, value) }));

  const post = async (path, body) => {
    setMsg(null);
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${bookingId}/${path}`,
        {
          method: "POST",
          body: JSON.stringify({ ...body, line_id: props.lineId }),
        },
      );
      if (res?.data?.booking) {
        props.onRefresh(res.data);
        setMsg({ ok: true, text: res.message });
      } else {
        setMsg({ ok: false, text: res?.message || t("error_body") });
      }
      return res;
    } catch {
      setMsg({ ok: false, text: t("error_body") });
      return null;
    }
  };

  const addItem = async (item, qty) => {
    if (item.kind === "rental") {
      await post("lines/add_extra_bed", {
        target_line_id: item.line_id,
        quantity: qty,
      });
    } else if (item.kind === "early_checkin") {
      await post("lines/add_early_checkin", { target_line_id: item.line_id });
    } else if (item.kind === "late_checkout") {
      const path =
        Number(item.adds_hours || 0) === 1
          ? "extend_late_checkout"
          : "add_late_checkout";
      const res = await post(`lines/${path}`, { target_line_id: item.line_id });
      const conflicts = res?.data?.conflicts || [];
      // Selling hours into the next guest's arrival is a double-booked room, not a pricing mistake —
      // the FO must be told who is coming before they can override.
      if (conflicts.length) {
        props.onConfirm({
          title: t("late_conflict_title"),
          body: `${conflicts.map((c) => c.message_id).join(" ")} ${t("late_conflict_ask")}`,
          confirmLabel: t("confirm_ok"),
          onConfirm: () =>
            post(`lines/${path}`, {
              target_line_id: item.line_id,
              acknowledge_conflicts: "1",
            }),
        });
      }
    } else {
      // Fail loudly. An unrecognised kind used to fall through every branch and reset the stepper,
      // which looks exactly like a successful add that vanished.
      setMsg({ ok: false, text: t("error_body") });
      return;
    }
    setDrafts((prev) => ({ ...prev, [item.key]: 0 }));
  };

  const removeItem = (item) =>
    props.onConfirm({
      title: t("pay_remove_title"),
      body: t("pay_remove_body"),
      confirmLabel: t("confirm_ok"),
      onConfirm: () => {
        if (item.kind === "rental")
          return post("lines/remove_add_on", { add_on_rental_id: item.id });
        if (item.kind === "early_checkin")
          return post("lines/remove_early_checkin", {
            target_line_id: item.line_id,
          });
        return post("lines/remove_late_checkout", {
          target_line_id: item.line_id,
          late_checkout_id: item.id,
        });
      },
    });

  // A pending extra bed steps IN PLACE — never release-and-re-reserve, which would hand the guest's
  // beds back to the pool while the change is in flight.
  const changeQuantity = async (item, qty) => {
    if (qty < 1) {
      removeItem(item);
      return;
    }
    setMsg(null);
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${bookingId}/rentals/${item.id}/quantity`,
        {
          method: "PATCH",
          body: JSON.stringify({ quantity: qty, line_id: props.lineId }),
        },
      );
      if (res?.data?.booking) props.onRefresh(res.data);
      else setMsg({ ok: false, text: res?.message || t("error_body") });
    } catch {
      setMsg({ ok: false, text: t("error_body") });
    }
  };

  // Sync / void / recreate / send, addressed by the opened payment's id.
  const lifecycle = async (action) => {
    if (action === "void") {
      props.onConfirm({
        title: t("pay_void_title"),
        body: t("pay_void_body"),
        confirmLabel: t("confirm_ok"),
        onConfirm: async () => {
          const res = await post(`payments/${opened.id}/void`, {});
          if (res?.data?.booking) props.onOpenPayment(null);
        },
      });
      return;
    }
    const res = await post(`payments/${opened.id}/${action}`, {});
    // The WhatsApp send is a wa.me deep link the FO's own device opens — nothing is dispatched from
    // the server, so a wrong number cannot reach a stranger.
    if (res?.data?.wa_url) {
      window.open(res.data.wa_url, "_blank", "noopener,noreferrer");
    }
  };

  // The dialog does NOT close after a successful charge — it re-renders as that payment's detail.
  // This is the moment the FO needs the link and the WhatsApp button, and closing here forced them to
  // find the row they had just created before they could send it.
  const settle = async (mode) => {
    const res = await post("charge_folio", {
      ...selection,
      mode,
      payment_method: "cash",
    });
    const created = res?.data?.payment?.payments?.[0];
    if (created) props.onOpenPayment(created.id);
  };

  const reviewDisabled =
    !discountConfig?.enabled || discountConfig.already_applied;

  return (
    <Backdrop onClick={props.onClose}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <HeadMain>
            <Title>
              <Receipt size={16} />
              {opened ? t("pay_detail_title") : t("pay_title")}
            </Title>
            <Sub>
              {data.guest?.full_name} ·{" "}
              {data.booking?.voucher_code || data.booking?.id?.slice(0, 8)}
            </Sub>
          </HeadMain>
          <Close type="button" onClick={props.onClose} aria-label={t("close")}>
            <X size={18} />
          </Close>
        </Head>

        {opened ? null : <State $kind={intent.kind}>{props.stateLabel}</State>}

        <Body>
          {msg ? <Msg $ok={msg.ok}>{msg.text}</Msg> : null}
          {opened ? (
            <PaymentDetail
              payment={opened}
              busy={props.busy}
              onAction={lifecycle}
            />
          ) : (
            <>
              <FolioEditor
                groups={groups}
                busy={props.busy}
                isPicked={isPicked}
                onTogglePick={togglePick}
                draftQty={draftQty}
                onDraftQty={setDraftQty}
                onAdd={addItem}
                onRemove={removeItem}
                onChangeQuantity={changeQuantity}
              />

              <Discounts>
                {quote.voucher_allowed ? (
                  <VoucherRow>
                    <VoucherInput
                      value={voucherInput}
                      placeholder={t("voucher_placeholder")}
                      onChange={(event) =>
                        setVoucherInput(event.target.value.toUpperCase())
                      }
                    />
                    <SmallBtn
                      type="button"
                      disabled={loading || !voucherInput.trim()}
                      onClick={() => setVoucher(voucherInput.trim())}
                    >
                      {t("voucher_apply")}
                    </SmallBtn>
                    {voucher ? (
                      <SmallBtn
                        type="button"
                        onClick={() => {
                          setVoucher("");
                          setVoucherInput("");
                        }}
                      >
                        {t("voucher_clear")}
                      </SmallBtn>
                    ) : null}
                  </VoucherRow>
                ) : null}
                {quote.voucher_reason ? (
                  <Note>{quote.voucher_reason}</Note>
                ) : null}

                {discountConfig?.enabled ? (
                  <Check
                    type="button"
                    aria-pressed={review}
                    disabled={reviewDisabled}
                    onClick={() => setReview(!review)}
                    title={t("review_discount_hint")}
                  >
                    <Box $on={review}>{review ? "✓" : null}</Box>
                    {discountConfig.already_applied
                      ? t("review_discount_used")
                      : `${t("review_discount")} ${discountConfig.percentage}%`}
                  </Check>
                ) : null}
              </Discounts>
            </>
          )}
        </Body>

        {opened ? (
          <BackRow>
            <Back type="button" onClick={() => props.onOpenPayment(null)}>
              {t("pay_back_to_charges")}
            </Back>
          </BackRow>
        ) : (
          <QuoteFooter
            quote={quote}
            heldTotal={heldTotal}
            hasSelection={chosen.length > 0}
            loading={loading}
            busy={props.busy}
            onSettle={settle}
            onCancel={props.onClose}
          />
        )}
      </Card>
    </Backdrop>
  );
};

export default PaymentDialog;
